import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { SplitText } from '@/components/motion/split-text'

interface HeroHeadingProps {
    first: string
    connector: string
    second: string
    align?: 'center' | 'start'
    tone?: 'dark' | 'light'
    className?: string
}

export const HeroHeading: FC<HeroHeadingProps> = ({
    first,
    connector,
    second,
    align = 'center',
    tone = 'dark',
    className
}): ReactNode => {
    return (
        <h1
            aria-label={`${first} ${connector} ${second}`}
            className={cn(
                'flex select-none flex-col overflow-clip',
                align === 'center' ? 'items-center' : 'items-start',
                tone === 'light' ? 'text-cream' : 'text-foreground',
                className
            )}
        >
            <motion.span
                className='block'
                initial={{ y: 106 }}
                animate={{ y: 0 }}
                transition={{ type: 'spring', bounce: 0.1, duration: 1.4, delay: 0.2 }}
            >
                <SplitText
                    text={first}
                    effect='flip'
                    trigger='mount'
                    delay={0.2}
                    stagger={0.03}
                    accessible={false}
                    className='t-display block whitespace-pre'
                />
            </motion.span>

            {' '}

            <span className='flex w-min items-center gap-1 overflow-clip md:gap-4'>
                <motion.span
                    className='block'
                    initial={{ x: 108 }}
                    animate={{ x: 0 }}
                    transition={{ type: 'spring', bounce: 0.2, duration: 1, delay: 0.4 }}
                >
                    <SplitText
                        text={connector}
                        effect='slide'
                        trigger='mount'
                        delay={0.4}
                        accessible={false}
                        className='t-display-serif block whitespace-pre'
                    />
                </motion.span>

                {' '}

                <motion.span
                    className='block'
                    initial={{ x: 108 }}
                    animate={{ x: 0 }}
                    transition={{ type: 'spring', bounce: 0.2, duration: 1, delay: 0.6 }}
                >
                    <SplitText
                        text={second}
                        effect='slide'
                        trigger='mount'
                        delay={0.6}
                        accessible={false}
                        className='t-display block whitespace-pre'
                    />
                </motion.span>
            </span>
        </h1>
    )
}
